/**
 * CreateSourceDialog — shared "Add source" dialog used by the Data Sources
 * and Civic Scraper pages. Extracted from the Data Sources page (same
 * UI/UX: analyze, jurisdiction typeahead, crawl config, advanced JSON).
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Search,
  X,
  MapPin,
  Sparkles,
} from 'lucide-react';
import {
  useJurisdictionSearch,
  resolveGeoPlaceCounty,
  type GeoPlaceResult,
} from '@/hooks/useJurisdictionSearch';
import { apiFetch } from '@/lib/apiFetch';
import type { Source } from '@/hooks/useSources';

const SOURCE_TYPES = [
  { value: 'html_page', label: 'HTML / Web Page' },
  { value: 'document_library', label: 'Document Library' },
  { value: 'rss', label: 'RSS / Atom Feed' },
  { value: 'agenda_portal', label: 'Agenda Portal' },
  { value: 'minutes_archive', label: 'Minutes Archive' },
  { value: 'video_feed', label: 'Video Feed' },
  { value: 'api_legistar', label: 'API — Legistar' },
  { value: 'api_granicus', label: 'API — Granicus' },
];

/**
 * Patterns that indicate a JS-heavy platform where Playwright is strongly
 * preferred over the simple fetch+Cheerio engine.
 */
const PLAYWRIGHT_URL_PATTERNS: { pattern: RegExp; label: string }[] = [
  { pattern: /civicplus\.com/i, label: 'CivicPlus' },
  { pattern: /\/AgendaCenter\//i, label: 'CivicPlus AgendaCenter' },
  { pattern: /\/DocumentCenter\//i, label: 'CivicPlus DocumentCenter' },
  { pattern: /civicweb\.net/i, label: 'CivicWeb' },
  { pattern: /granicus\.com/i, label: 'Granicus' },
  { pattern: /legistar\.com/i, label: 'Legistar' },
  { pattern: /novusagenda/i, label: 'Novus Agenda' },
  { pattern: /boarddocs\.com/i, label: 'BoardDocs' },
  { pattern: /iqm2\.com/i, label: 'IQM2' },
  { pattern: /municode\.com/i, label: 'Municode' },
  { pattern: /laserfiche/i, label: 'Laserfiche' },
  { pattern: /destinyhosted\.com/i, label: 'Destiny Hosted' },
];

/**
 * Look up a GeoPlaceResult by FIPS code.
 * Uses the jurisdiction search endpoint and finds the exact FIPS match.
 */
async function lookupGeoPlaceByFips(
  fips: string
): Promise<GeoPlaceResult | null> {
  try {
    const res = await apiFetch(
      `/api/admin/geo-places/${encodeURIComponent(fips)}`
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      success: boolean;
      data: GeoPlaceResult;
    };
    return json.data ?? null;
  } catch {
    return null;
  }
}

async function lookupJurisdictionFips(
  jurisdictionId: string
): Promise<string | null> {
  try {
    const res = await apiFetch(`/api/jurisdictions/${jurisdictionId}`);
    if (!res.ok) return null;
    const json = (await res.json()) as {
      success: boolean;
      data: { fips_code?: string | null };
    };
    return json.data?.fips_code ?? null;
  } catch {
    return null;
  }
}

/** Returns a platform label if the URL suggests Playwright is needed, else null. */
function detectPlaywrightPlatform(url: string): string | null {
  if (!url) return null;
  for (const { pattern, label } of PLAYWRIGHT_URL_PATTERNS) {
    if (pattern.test(url)) return label;
  }
  return null;
}

export const SCHEDULES = [
  { value: 'none', label: 'Manual only' },
  { value: '0 6 * * *', label: 'Daily at 6 AM' },
  { value: '0 */6 * * *', label: 'Every 6 hours' },
  { value: '0 * * * *', label: 'Hourly' },
  { value: '0 6 * * 1', label: 'Weekly (Mon 6 AM)' },
];

/** Convert the form schedule value to a DB-safe cron string or null */

function fromScheduleCron(v: string | null | undefined): string {
  return v ?? 'none';
}

// ── Source form ───────────────────────────────────────────────────────────────

interface CrawlConfigFields {
  engine: 'fetch' | 'playwright';
  max_pages: number;
  politeness_delay_ms: number;
  file_extensions: string[];
  capture_html: boolean;
  /** CivicPlus: earliest year to fetch via UpdateCategoryList. 0 = not set (use default). */
  civicplus_start_year: number;
  /** Playwright page.goto wait strategy. Only applies when engine is 'playwright'. */
  goto_wait_until: 'load' | 'domcontentloaded' | 'networkidle';
  /**
   * Platform adapter for structured discovery (CivicPlus /Search/, Legistar,
   * Granicus RSS, PrimeGov). '' = auto-detect from the source URL.
   */
  platform: string;
  /**
   * Platform discovery window start ("YYYY-MM-DD"). '' = pilot default
   * 2025-01-01 (router.PILOT_START_DATE).
   */
  history_start_date: string;
  advanced_json: string;
}

/** Platform adapter options shown in the crawler form. */
const PLATFORM_OPTIONS: Array<{ id: string; label: string }> = [
  { id: '', label: 'Auto-detect' },
  { id: 'civic-plus', label: 'CivicPlus (AgendaCenter /Search/)' },
  { id: 'legistar', label: 'Legistar' },
  { id: 'granicus', label: 'Granicus (RSS feed URL)' },
  { id: 'primegov', label: 'PrimeGov' },
];

// All capturable extensions grouped for the UI.
// Defaults: PDF + Word docs pre-checked; HTML, images, and media are opt-in.
const FILE_TYPE_GROUPS = [
  {
    label: 'Documents',
    exts: [
      { ext: 'pdf', label: 'PDF', default: true },
      { ext: 'doc', label: 'Word (.doc)', default: true },
      { ext: 'docx', label: 'Word (.docx)', default: true },
      { ext: 'xlsx', label: 'Excel (.xlsx)', default: true },
      { ext: 'xls', label: 'Excel (.xls)', default: true },
      { ext: 'csv', label: 'CSV', default: true },
    ],
  },
  {
    label: 'HTML Pages',
    exts: [{ ext: 'html', label: 'HTML page', default: false }],
  },
  {
    label: 'Images',
    exts: [
      { ext: 'jpg', label: 'JPG', default: false },
      { ext: 'png', label: 'PNG', default: false },
      { ext: 'gif', label: 'GIF', default: false },
      { ext: 'webp', label: 'WebP', default: false },
    ],
  },
  {
    label: 'Audio & Video',
    exts: [
      { ext: 'mp3', label: 'MP3', default: true },
      { ext: 'wav', label: 'WAV', default: true },
      { ext: 'm4a', label: 'M4A', default: true },
      { ext: 'mp4', label: 'MP4', default: true },
      { ext: 'avi', label: 'AVI', default: true },
      { ext: 'mov', label: 'MOV', default: true },
    ],
  },
];

interface SourceFormData {
  name: string;
  url: string;
  source_type: string;
  schedule_cron: string;
  jurisdiction_id: string;
  /** FIPS code of the geo_place selected via typeahead (persisted to sources.jurisdiction_fips). */
  jurisdiction_fips: string;
  crawl: CrawlConfigFields;
}

const DEFAULT_CRAWL: CrawlConfigFields = {
  engine: 'fetch',
  max_pages: 50,
  politeness_delay_ms: 2000,
  file_extensions: [
    'pdf',
    'doc',
    'docx',
    'xlsx',
    'xls',
    'csv',
    'mp3',
    'wav',
    'm4a',
    'mp4',
    'avi',
    'mov',
  ],

  capture_html: false,
  civicplus_start_year: 0,
  goto_wait_until: 'networkidle',
  platform: '',
  history_start_date: '',
  advanced_json: '',
};

const EMPTY_FORM: SourceFormData = {
  name: '',
  url: '',
  source_type: 'html_page',
  schedule_cron: 'none',
  jurisdiction_id: '',
  jurisdiction_fips: '',
  crawl: { ...DEFAULT_CRAWL },
};

/** Build the crawl_config object saved to the DB */
export function buildCrawlConfig(
  c: CrawlConfigFields
): Record<string, unknown> {
  // Count total available extensions across all groups
  const totalExts = FILE_TYPE_GROUPS.flatMap((g) => g.exts).length;
  // When all types are selected there is no restriction — omit file_extensions
  const allSelected = c.file_extensions.length >= totalExts;

  // Always store without leading dots for consistency
  const cleanExts = c.file_extensions.map((e) => e.replace(/^\./, ''));

  // UI-generated fields (always derived from the form controls)
  const uiFields: Record<string, unknown> = {
    engine: c.engine,
    max_pages: c.max_pages,
    politeness_delay_ms: c.politeness_delay_ms,
    capture_html: c.capture_html,
    ...(!allSelected && { file_extensions: cleanExts }),
    // Only include civicplus_start_year when explicitly set (non-zero)
    ...(c.civicplus_start_year > 0 && {
      civicplus_start_year: c.civicplus_start_year,
    }),
    // Only include goto_wait_until when changed from default
    ...(c.goto_wait_until !== 'networkidle' && {
      goto_wait_until: c.goto_wait_until,
    }),
    // Platform adapter + discovery window — only when explicitly set
    ...(c.platform && { platform: c.platform }),
    ...(c.history_start_date && { history_start_date: c.history_start_date }),
  };

  // Advanced JSON is the base; UI fields are merged on top (UI wins on conflict).
  if (c.advanced_json.trim()) {
    try {
      const base = JSON.parse(c.advanced_json) as Record<string, unknown>;
      return { ...base, ...uiFields };
    } catch {
      // Invalid JSON — fall through and use UI fields only
    }
  }

  return uiFields;
}

interface SourceDialogProps {
  initial?: Source;
  onSave: (data: SourceFormData) => Promise<unknown>;
  trigger: React.ReactNode;
  title: string;
  description: string;
  /** Controlled open state — when provided the dialog becomes controlled. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SourceDialog({
  initial,
  onSave,
  trigger,
  title,
  description,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: SourceDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  // Use controlled open if provided, otherwise fall back to internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (controlledOnOpenChange) controlledOnOpenChange(next);
    else setInternalOpen(next);
  };
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ── Jurisdiction typeahead state ──────────────────────────────────────────
  const [jurQuery, setJurQuery] = useState('');
  const [jurDropdownOpen, setJurDropdownOpen] = useState(false);
  const [selectedGeoPlace, setSelectedGeoPlace] =
    useState<GeoPlaceResult | null>(null);
  const [countyResolving, setCountyResolving] = useState(false);
  const [resolvedCounty, setResolvedCounty] = useState<string | null>(null);
  const [countyFailed, setCountyFailed] = useState(false);
  const jurInputRef = useRef<HTMLInputElement>(null);
  const jurDropdownRef = useRef<HTMLDivElement>(null);

  const { results: jurResults, loading: jurSearching } = useJurisdictionSearch(
    jurDropdownOpen ? jurQuery : ''
  );

  function buildInitialForm(): SourceFormData {
    const crawl: CrawlConfigFields = initial?.crawl_config
      ? {
          engine:
            (initial.crawl_config.engine as 'fetch' | 'playwright') ?? 'fetch',
          max_pages: (initial.crawl_config.max_pages as number) ?? 50,
          politeness_delay_ms:
            (initial.crawl_config.politeness_delay_ms as number) ?? 2000,
          file_extensions: Array.isArray(initial.crawl_config.file_extensions)
            ? (initial.crawl_config.file_extensions as string[]).map((e) =>
                e.replace(/^\./, '')
              )
            : typeof initial.crawl_config.file_extensions === 'string'
              ? (initial.crawl_config.file_extensions as string)
                  .split(',')
                  .map((e) => e.trim().replace(/^\./, ''))
                  .filter(Boolean)
              : DEFAULT_CRAWL.file_extensions,
          capture_html: (initial.crawl_config.capture_html as boolean) ?? false,
          civicplus_start_year:
            (initial.crawl_config.civicplus_start_year as number) ?? 0,
          goto_wait_until:
            (initial.crawl_config
              .goto_wait_until as CrawlConfigFields['goto_wait_until']) ??
            'networkidle',
          platform: (initial.crawl_config.platform as string) ?? '',
          history_start_date:
            (initial.crawl_config.history_start_date as string) ?? '',
          advanced_json: JSON.stringify(initial.crawl_config, null, 2),
        }
      : { ...DEFAULT_CRAWL };

    return initial
      ? {
          name: initial.name,
          url: initial.base_url,
          source_type: initial.source_type,
          schedule_cron: fromScheduleCron(initial.schedule_cron),
          jurisdiction_id: initial.jurisdiction_id ?? '',
          jurisdiction_fips: initial.jurisdiction_fips ?? '',
          crawl,
        }
      : EMPTY_FORM;
  }

  const [form, setForm] = useState<SourceFormData>(buildInitialForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [playwrightHintDismissed, setPlaywrightHintDismissed] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<{
    platform_label: string | null;
    engine: string;
    source_type: string;
    file_extensions: string[];
    warnings: string[];
    response_code: number;
  } | null>(null);

  // Re-populate the form with fresh data every time the dialog opens
  function handleOpenChange(next: boolean) {
    if (next) {
      const f = buildInitialForm();
      setForm(f);
      setErr(null);
      setShowAdvanced(false);
      setPlaywrightHintDismissed(false);
      setJurQuery('');
      setJurDropdownOpen(false);
      setResolvedCounty(null);
      setCountyFailed(false);
      setSelectedGeoPlace(null);
      setAnalyzing(false);
      setAnalysisResult(null);
    }
    setOpen(next);
  }

  async function handleAnalyze() {
    const url = form.url.trim();
    if (!url) {
      setErr('Enter a URL first.');
      return;
    }
    try {
      new URL(url);
    } catch {
      setErr('Enter a valid URL first.');
      return;
    }
    setErr(null);
    setAnalysisResult(null);
    setAnalyzing(true);
    try {
      const res = await apiFetch('/api/sources/analyze', {
        method: 'POST',
        body: JSON.stringify({ url }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error((e as { error?: string }).error ?? 'Analysis failed');
      }
      const json = await res.json();
      const data = json.data as {
        engine: 'fetch' | 'playwright';
        source_type: string;
        platform_label: string | null;
        warnings?: string[];
        response_code?: number;
        server?: string | null;
        crawl_config: {
          engine: string;
          max_pages: number;
          politeness_delay_ms: number;
          file_extensions: string[];
          capture_html: boolean;
          goto_wait_until?: string;
        };
      };

      setCrawl({
        engine: data.crawl_config.engine as 'fetch' | 'playwright',
        max_pages: data.crawl_config.max_pages,
        politeness_delay_ms: data.crawl_config.politeness_delay_ms,
        file_extensions: data.crawl_config.file_extensions,
        capture_html: data.crawl_config.capture_html,
        ...(data.crawl_config.goto_wait_until && {
          goto_wait_until: data.crawl_config
            .goto_wait_until as CrawlConfigFields['goto_wait_until'],
        }),
      });
      setForm((f) => ({ ...f, source_type: data.source_type }));

      setAnalysisResult({
        platform_label: data.platform_label,
        engine: data.engine,
        source_type: data.source_type,
        file_extensions: data.crawl_config.file_extensions,
        warnings: data.warnings ?? [],
        response_code: data.response_code ?? 0,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to analyze URL');
    } finally {
      setAnalyzing(false);
    }
  }

  // When editing an existing source with a known jurisdiction, look up the
  // geo_place so the pill (name · type · state) is displayed in the form.
  useEffect(() => {
    if (!open) return;
    const fips = initial?.jurisdiction_fips;
    const jurId = initial?.jurisdiction_id;
    if (!fips && !jurId) return;
    let cancelled = false;
    (async () => {
      let resolvedFips = fips;
      if (!resolvedFips && jurId) {
        resolvedFips = await lookupJurisdictionFips(jurId);
      }
      if (cancelled || !resolvedFips) return;
      const place = await lookupGeoPlaceByFips(resolvedFips);
      if (cancelled || !place) return;
      setSelectedGeoPlace(place);
      if (place.county) {
        setResolvedCounty(place.county);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, initial?.jurisdiction_fips, initial?.jurisdiction_id]);

  // Controlled-open rebuild: when the dialog is opened with `open` (no
  // trigger interaction — e.g. the Civic Scraper per-row [+] which uses a
  // span trigger) the form would stay at its mount-time initial state.
  // Rebuild the form from `initial` each time the dialog opens for a new
  // row/entity without clobbering field edits within an open session.
  const formKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const key = initial?.id ?? 'new-source';
    if (formKeyRef.current === key) return;
    formKeyRef.current = key;
    setForm(buildInitialForm());
    setErr(null);
    setShowAdvanced(false);
    setPlaywrightHintDismissed(false);
    setJurQuery('');
    setJurDropdownOpen(false);
    setResolvedCounty(null);
    setCountyFailed(false);
    setSelectedGeoPlace(null);
    setAnalyzing(false);
    setAnalysisResult(null);
  }, [open, initial]);

  const setCrawl = (patch: Partial<CrawlConfigFields>) =>
    setForm((f) => ({ ...f, crawl: { ...f.crawl, ...patch } }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) {
      setErr('Name and URL are required.');
      return;
    }
    if (form.crawl.advanced_json.trim()) {
      try {
        JSON.parse(form.crawl.advanced_json);
      } catch {
        setErr('Advanced JSON is not valid JSON.');
        return;
      }
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave(form);
      setOpen(false);
      setForm(EMPTY_FORM);
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      {/* Force light theme for this dialog regardless of global CSS variables */}
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto bg-white text-gray-900 border-gray-200">
        <DialogHeader>
          <DialogTitle className="text-gray-900">{title}</DialogTitle>
          <DialogDescription className="text-gray-500">
            {description}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          {/* ── Basic fields ── */}
          <div className="space-y-1">
            <Label htmlFor="src-name" className="text-gray-700">
              Source name *
            </Label>
            <Input
              id="src-name"
              placeholder="e.g. Riverside County Agendas"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="src-url" className="text-gray-700">
              URL *
            </Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  id="src-url"
                  type="url"
                  placeholder="https://countysite.gov/agendas"
                  value={form.url}
                  onChange={(e) => {
                    setForm({ ...form, url: e.target.value });
                    setPlaywrightHintDismissed(false);
                    setAnalysisResult(null);
                  }}
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                size="default"
                disabled={analyzing || !form.url.trim()}
                onClick={handleAnalyze}
                className="shrink-0 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:text-blue-800"
              >
                {analyzing ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1.5" />
                )}
                Auto-detect
              </Button>
            </div>
            {analysisResult && (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-500" />
                  <div>
                    <span className="font-medium text-green-800">
                      Parameters detected
                    </span>
                    {analysisResult.platform_label && (
                      <span className="ml-1 text-green-700">
                        — {analysisResult.platform_label}
                      </span>
                    )}
                  </div>
                </div>
                {analysisResult.warnings.length > 0 && (
                  <div className="mt-1.5 ml-6 space-y-0.5">
                    {analysisResult.warnings.map((w, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-1.5 text-amber-700"
                      >
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-gray-700">Source type</Label>
              <Select
                value={form.source_type}
                onValueChange={(v) => setForm({ ...form, source_type: v })}
              >
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {SOURCE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-gray-700">Scrape schedule</Label>
              <Select
                value={form.schedule_cron}
                onValueChange={(v) => setForm({ ...form, schedule_cron: v })}
              >
                <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Manual only" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {SCHEDULES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Jurisdiction typeahead ── */}
          <div className="space-y-1">
            <Label className="text-gray-700">Jurisdiction</Label>

            {/* Selected pill — shown when user has picked a place */}
            {selectedGeoPlace ? (
              <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
                <MapPin className="w-4 h-4 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-900 text-sm">
                    {selectedGeoPlace.name}
                  </span>
                  <span className="text-gray-500 text-sm ml-1.5">
                    {selectedGeoPlace.jurisdiction_type} ·{' '}
                    {selectedGeoPlace.state}
                  </span>
                  {/* Linked jurisdiction badge */}
                  {selectedGeoPlace.linked_jurisdiction_id && (
                    <Badge
                      variant="outline"
                      className="ml-2 text-xs border-green-300 text-green-700 bg-green-50"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Linked
                    </Badge>
                  )}
                  {/* County — shown after lazy resolve */}
                  {countyResolving && (
                    <span className="ml-2 text-xs text-gray-400 flex items-center gap-1 inline-flex">
                      <Loader2 className="w-3 h-3 animate-spin" /> resolving
                      county…
                    </span>
                  )}
                  {!countyResolving && resolvedCounty && (
                    <span className="ml-2 text-xs text-gray-500">
                      {resolvedCounty} County
                    </span>
                  )}
                  {!countyResolving && countyFailed && (
                    <span
                      className="ml-2 text-xs text-amber-600"
                      title="County lookup unavailable — will retry later"
                    >
                      county unknown
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Clear jurisdiction"
                  className="text-gray-400 hover:text-gray-700 shrink-0"
                  onClick={() => {
                    setSelectedGeoPlace(null);
                    setResolvedCounty(null);
                    setCountyFailed(false);
                    setJurQuery('');
                    setForm((f) => ({
                      ...f,
                      jurisdiction_id: '',
                      jurisdiction_fips: '',
                    }));
                  }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              /* Search input */
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    ref={jurInputRef}
                    type="text"
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Search city, town, village…"
                    autoComplete="off"
                    value={jurQuery}
                    onFocus={() => setJurDropdownOpen(true)}
                    onChange={(e) => {
                      setJurQuery(e.target.value);
                      setJurDropdownOpen(true);
                    }}
                    onBlur={() => {
                      // Short delay so click on dropdown item registers first
                      setTimeout(() => setJurDropdownOpen(false), 150);
                    }}
                  />
                  {jurSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                  )}
                </div>

                {/* Dropdown results */}
                {jurDropdownOpen && jurQuery.trim().length >= 1 && (
                  <div
                    ref={jurDropdownRef}
                    className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
                  >
                    {/* Clear / unassigned option */}
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 border-b border-gray-100"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSelectedGeoPlace(null);
                        setForm((f) => ({
                          ...f,
                          jurisdiction_id: '',
                          jurisdiction_fips: '',
                        }));
                        setJurQuery('');
                        setJurDropdownOpen(false);
                      }}
                    >
                      — None / Unassigned —
                    </button>

                    {jurResults.length === 0 && !jurSearching && (
                      <p className="px-3 py-3 text-sm text-gray-400">
                        No places found for &ldquo;{jurQuery}&rdquo;
                      </p>
                    )}

                    {jurResults.map((place) => (
                      <button
                        key={place.fips_code}
                        type="button"
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 flex items-start justify-between gap-2"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={async () => {
                          setSelectedGeoPlace(place);
                          setJurDropdownOpen(false);
                          setJurQuery('');
                          // Wire form: link to existing jurisdiction if available
                          setForm((f) => ({
                            ...f,
                            jurisdiction_id:
                              place.linked_jurisdiction_id || f.jurisdiction_id,
                            jurisdiction_fips: place.fips_code,
                          }));
                          // Lazy-resolve county in the background
                          if (!place.county) {
                            setCountyResolving(true);
                            setResolvedCounty(null);
                            setCountyFailed(false);
                            const resolved = await resolveGeoPlaceCounty(
                              place.fips_code
                            );
                            setCountyResolving(false);
                            if (resolved.geocode_failed) {
                              setCountyFailed(true);
                            } else {
                              setResolvedCounty(resolved.county);
                            }
                          } else {
                            setResolvedCounty(place.county);
                          }
                        }}
                      >
                        <span className="flex items-baseline gap-1.5 min-w-0">
                          <span className="font-medium text-gray-900 truncate">
                            {place.name}
                          </span>
                          <span className="text-gray-500 shrink-0">
                            {place.jurisdiction_type} · {place.state}
                          </span>
                        </span>
                        {place.linked_jurisdiction_id && (
                          <Badge
                            variant="outline"
                            className="shrink-0 text-xs border-green-300 text-green-700 bg-green-50"
                          >
                            Linked
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-gray-500">
              Search for a US incorporated place (city, town, village…). The
              jurisdiction ID is resolved automatically via FIPS code.
            </p>
          </div>

          {/* ── Crawl config ── */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
            <p className="text-sm font-semibold text-gray-700">
              Crawl settings
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="src-engine" className="text-gray-700">
                  Engine
                </Label>
                <Select
                  value={form.crawl.engine}
                  onValueChange={(v) =>
                    setCrawl({ engine: v as 'fetch' | 'playwright' })
                  }
                >
                  <SelectTrigger
                    id="src-engine"
                    className="bg-white border-gray-300 text-gray-900"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">
                    <SelectItem value="fetch">
                      fetch + Cheerio (static)
                    </SelectItem>
                    <SelectItem value="playwright">
                      Playwright (JS-rendered)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.crawl.engine === 'playwright' && (
                <div className="space-y-1">
                  <Label htmlFor="src-goto" className="text-gray-700">
                    Wait strategy
                  </Label>
                  <Select
                    value={form.crawl.goto_wait_until}
                    onValueChange={(v) =>
                      setCrawl({
                        goto_wait_until:
                          v as CrawlConfigFields['goto_wait_until'],
                      })
                    }
                  >
                    <SelectTrigger
                      id="src-goto"
                      className="bg-white border-gray-300 text-gray-900"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      <SelectItem value="networkidle">
                        networkidle (default)
                      </SelectItem>
                      <SelectItem value="load">
                        load — page load event
                      </SelectItem>
                      <SelectItem value="domcontentloaded">
                        domcontentloaded — HTML parsed
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="src-maxpages" className="text-gray-700">
                  Max pages
                </Label>
                <Input
                  id="src-maxpages"
                  type="number"
                  min={1}
                  max={100000}
                  value={form.crawl.max_pages}
                  onChange={(e) =>
                    setCrawl({ max_pages: parseInt(e.target.value, 10) || 50 })
                  }
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="src-delay" className="text-gray-700">
                  Politeness delay (ms)
                </Label>
                <Input
                  id="src-delay"
                  type="number"
                  min={500}
                  step={500}
                  value={form.crawl.politeness_delay_ms}
                  onChange={(e) =>
                    setCrawl({
                      politeness_delay_ms: parseInt(e.target.value, 10) || 2000,
                    })
                  }
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
            </div>

            {/* Playwright recommendation banner */}
            {(() => {
              const platform = detectPlaywrightPlatform(form.url);
              if (
                !platform ||
                form.crawl.engine === 'playwright' ||
                playwrightHintDismissed
              )
                return null;
              return (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">
                      Playwright recommended for {platform}
                    </span>
                    <span className="ml-1 text-amber-700">
                      — this site uses JavaScript rendering. Switch the engine
                      above for best results.
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      className="text-xs font-medium text-amber-800 underline hover:no-underline"
                      onClick={() => setCrawl({ engine: 'playwright' })}
                    >
                      Use Playwright
                    </button>
                    <button
                      type="button"
                      aria-label="Dismiss"
                      className="text-amber-500 hover:text-amber-700"
                      onClick={() => setPlaywrightHintDismissed(true)}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Capture HTML toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.crawl.capture_html}
                onChange={(e) => setCrawl({ capture_html: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              <span>
                <span className="font-medium text-gray-900">
                  Archive HTML pages
                </span>
                <span className="text-gray-500 ml-1">
                  — uncheck to crawl for links only, without saving page HTML
                </span>
              </span>
            </label>

            {/* CivicPlus history depth — shown only when URL matches a CivicPlus site */}
            {detectPlaywrightPlatform(form.url)
              ?.toLowerCase()
              .includes('civicplus') && (
              <div className="space-y-1">
                <Label htmlFor="src-civicplus-year" className="text-gray-700">
                  CivicPlus history (start year)
                </Label>
                <Input
                  id="src-civicplus-year"
                  type="number"
                  min={2000}
                  max={new Date().getFullYear()}
                  placeholder={String(new Date().getFullYear() - 5)}
                  value={form.crawl.civicplus_start_year || ''}
                  onChange={(e) =>
                    setCrawl({
                      civicplus_start_year: parseInt(e.target.value, 10) || 0,
                    })
                  }
                  className="bg-white border-gray-300 text-gray-900 w-32"
                />
                <p className="text-xs text-gray-500">
                  Earliest year to fetch from AgendaCenter (default:{' '}
                  {new Date().getFullYear() - 5}). Set to e.g.{' '}
                  <span className="font-mono">2016</span> for a deep historical
                  pull.
                </p>
              </div>
            )}

            {/* Platform structured discovery — additive; runs before the crawl */}
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div>
                <Label htmlFor="src-platform" className="text-gray-700">
                  Platform discovery
                </Label>
                <select
                  id="src-platform"
                  data-testid="platform-select"
                  value={form.crawl.platform}
                  onChange={(e) => setCrawl({ platform: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                >
                  {PLATFORM_OPTIONS.map((opt) => (
                    <option key={opt.id || 'auto'} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Structured discovery of meeting documents + metadata (meeting
                  date, committee, agenda/minutes) before the crawl. Auto-detect
                  uses the URL; choose a platform manually for sites whose URL
                  carries no marker (e.g. a CivicPlus homepage). Granicus
                  requires the <span className="font-mono">.rss</span> feed URL.
                </p>
              </div>
              <div>
                <Label htmlFor="src-history-date" className="text-gray-700">
                  Discovery start date
                </Label>
                <Input
                  id="src-history-date"
                  type="date"
                  value={form.crawl.history_start_date || ''}
                  onChange={(e) =>
                    setCrawl({ history_start_date: e.target.value })
                  }
                  className="bg-white border-gray-300 text-gray-900 w-40"
                />
                <p className="text-xs text-gray-500">
                  Earliest date to discover (pilot default:{' '}
                  <span className="font-mono">2025-01-01</span>). Extend
                  backward in yearly windows once the extraction queue drains.
                </p>
              </div>
            </div>

            {/* File types — grouped checkbox panel */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-gray-700">File types to capture</Label>
                <div className="flex gap-3 text-xs text-blue-600">
                  <button
                    type="button"
                    className="hover:underline"
                    onClick={() =>
                      setCrawl({
                        file_extensions: FILE_TYPE_GROUPS.flatMap((g) =>
                          g.exts.map((e) => e.ext)
                        ),
                      })
                    }
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className="hover:underline"
                    onClick={() => setCrawl({ file_extensions: [] })}
                  >
                    None
                  </button>
                  <button
                    type="button"
                    className="hover:underline"
                    onClick={() =>
                      setCrawl({
                        file_extensions: FILE_TYPE_GROUPS.flatMap((g) =>
                          g.exts.filter((e) => e.default).map((e) => e.ext)
                        ),
                      })
                    }
                  >
                    Default
                  </button>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white">
                {FILE_TYPE_GROUPS.map((group) => (
                  <div key={group.label} className="px-3 py-2 space-y-1.5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {group.label}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                      {group.exts.map(({ ext, label }) => {
                        const checked =
                          form.crawl.file_extensions.includes(ext);
                        return (
                          <label
                            key={ext}
                            className="flex items-center gap-1.5 cursor-pointer select-none text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const next = checked
                                  ? form.crawl.file_extensions.filter(
                                      (x) => x !== ext
                                    )
                                  : [...form.crawl.file_extensions, ext];
                                setCrawl({ file_extensions: next });
                              }}
                              className="rounded border-gray-300 text-blue-600"
                            />
                            <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded text-gray-600">
                              .{ext}
                            </span>
                            <span className="text-gray-700">{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {form.crawl.file_extensions.length === 0 && (
                <p className="text-xs text-amber-600">
                  ⚠ No file types selected — only HTML pages will be archived.
                </p>
              )}
            </div>

            {/* Advanced JSON toggle */}
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? '▲ Hide' : '▼ Show'} advanced JSON override
            </button>
            {showAdvanced && (
              <div className="space-y-1">
                <Label htmlFor="src-json" className="text-gray-700">
                  Advanced crawl_config (JSON — overrides fields above)
                </Label>
                <textarea
                  id="src-json"
                  className="w-full font-mono text-xs border border-gray-300 rounded p-2 h-28 resize-y bg-white text-gray-900 placeholder:text-gray-400"
                  placeholder='{"engine":"fetch","max_pages":50,"link_patterns":[".*\\.pdf$"]}'
                  value={form.crawl.advanced_json}
                  onChange={(e) => setCrawl({ advanced_json: e.target.value })}
                />
              </div>
            )}
          </div>

          {err && (
            <p className="flex items-center gap-1 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {err}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save source
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Jobs panel ────────────────────────────────────────────────────────────────
